import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { SEO } from '@/components/SEO';
import { SocialShare } from '@/components/SocialShare';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Calendar, Clock, User } from 'lucide-react';

const OfflineGatherings = () => {
  const article = {
    title: "The Quiet Rebellion: Why We're Choosing to Be Offline — Together",
    date: "September 13, 2025",
    readTime: "12 min read",
    author: "Piotr",
    category: "Digital Wellness",
    image: "/lovable-uploads/offline-gathering-hands.jpg",
    imageCredit: "Pinterest/Open Hands Creative"
  };

  return (
    <>
      <SEO 
        title={article.title}
        description="Exploring the cultural rise of intentional offline gatherings — a growing movement towards analog, in-person experiences as a counterbalance to digital saturation."
        canonicalUrl="https://mysticforest.club/articles/offline-gatherings"
        ogType="article"
        ogImage={`https://mysticforest.club${article.image}`}
        article={{
          publishedTime: "2025-10-15T00:00:00Z",
          author: article.author,
          section: article.category,
          tags: ["digital wellness", "offline gatherings", "mindfulness", "community", "analog culture"]
        }}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <article className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Breadcrumbs items={[
              { name: "Articles", url: "/blog" },
              { name: article.title, url: "/articles/offline-gatherings" }
            ]} />
            
            {/* Article Header */}
            <header className="mb-12">
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <time dateTime="2025-09-13">{article.date}</time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{article.readTime}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>By {article.author}</span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-serif font-light mb-8 text-foreground leading-tight">
                {article.title}
              </h1>

              <div className="relative aspect-video w-full overflow-hidden rounded-lg mb-4">
                <img 
                  src={article.image}
                  alt="Hands coming together in offline gathering"
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              <p className="text-sm text-muted-foreground italic text-center">
                Photo: {article.imageCredit}
              </p>
            </header>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "The opposite of addiction is not sobriety. The opposite of addiction is connection."
                <footer className="text-sm mt-2">— Johann Hari, Lost Connections: Why You're Depressed and How to Find Hope (London: Bloomsbury, 2018), p. 249</footer>
              </blockquote>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">A Strange New Trend: Meeting Without Screens</h2>

              <p className="mb-6 leading-relaxed">
                Across cities and small towns, something quietly radical is happening.
                People are gathering — without phones, without Wi-Fi, without agendas.
              </p>

              <p className="mb-6 leading-relaxed">
                They meet to write letters by hand, to draw, to meditate, to talk.
                To sit in silence. To walk. To be together — fully, undistractedly.
              </p>

              <p className="mb-6 leading-relaxed">
                In an era obsessed with being online, a new form of connection is emerging: intentional offline gatherings.
                Not as nostalgia, but as necessity.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">From Constant Connection to Conscious Disconnection</h2>

              <p className="mb-6 leading-relaxed">
                The average adult spends over 6.5 hours per day online (DataReportal, 2024).
                For many, that's more waking time than we spend with other humans in the same room.
                Technology promised connection, yet loneliness rates keep rising.
              </p>

              <p className="mb-6 leading-relaxed">
                A 2023 study by the American Psychological Association found that 59% of young adults report feeling "seriously lonely," despite constant digital communication.
              </p>

              <p className="mb-6 leading-relaxed">
                The human nervous system — wired for rhythm and relational presence — wasn't designed for perpetual notifications.
                We need texture, tone, and silence.
                And that's what the new wave of offline gatherings is quietly restoring.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Rise of Analog Culture</h2>

              <p className="mb-6 leading-relaxed">
                What began as small experiments — friends hosting no-phone dinners or silent walks — has become a cultural current.
              </p>

              <p className="mb-6 leading-relaxed">
                Analog film photography is back. Vinyl sales are the highest in three decades (RIAA 2023).
                Letter-writing workshops in Berlin, Copenhagen, and Tokyo sell out within hours.
                Even corporate teams seek retreats built on conversation and breath, not slides and Slack.
              </p>

              <p className="mb-6 leading-relaxed">
                Psychologists describe this as a reaction to cognitive overload.
                When every moment is captured and shared, the only authentic thing left is what's not documented.
              </p>

              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "We crave experiences that resist replication. The screen gives us connection at a distance, but what we long for is the messy, unedited human encounter."
                <footer className="text-sm mt-2">— Sherry Turkle, Reclaiming Conversation: The Power of Talk in a Digital Age (New York: Penguin Press, 2015), pp. 28–29</footer>
              </blockquote>

              <p className="mb-6 leading-relaxed">
                Offline gatherings are becoming that safe space — small, intentional sanctuaries from the constant performance of being seen.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Why We Need Analog Belonging</h2>

              <p className="mb-6 leading-relaxed">
                From a neurological perspective, offline connection does something technology cannot replicate: it synchronizes.
              </p>

              <p className="mb-6 leading-relaxed">
                Research by Dr. Stephen Porges on the polyvagal theory shows that face-to-face presence regulates the nervous system, lowering heart rate and cortisol while releasing oxytocin — the hormone linked to trust and calm.
                This is why even short, real-world interactions feel grounding, while long hours online leave us overstimulated and empty.
              </p>

              <p className="mb-6 leading-relaxed">
                Offline experiences are not anti-digital. They're pro-human.
                They restore what digital life has fragmented: rhythm, empathy, and embodied attention.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Examples of a Growing Movement</h2>

              <p className="mb-4 leading-relaxed">
                Around the world, communities are rediscovering what it means to gather with intention:
              </p>

              <ul className="space-y-3 mb-6">
                <li>• <strong>Silent walks</strong> — groups walk in complete silence, focusing on breath and sound.</li>
                <li>• <strong>Analog evenings</strong> — reading, drawing, or simply sharing space without phones.</li>
                <li>• <strong>Letter-writing circles</strong> — exchanging handwritten notes as slow communication.</li>
                <li>• <strong>Campfire dialogues</strong> — conversations with no topic, just presence.</li>
                <li>• <strong>Screen-free Sundays</strong> — families dedicating one day each week to offline rituals.</li>
              </ul>

              <p className="mb-6 leading-relaxed">
                Small gestures, perhaps, but collectively they signal something big:
                a cultural yearning for depth in an age of distraction.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Paradox of Digital Detox</h2>

              <p className="mb-6 leading-relaxed">
                The phrase digital detox has been around for years, yet most people treat it as a temporary cleanse — a short break before diving back into the noise.
                What's emerging now is different.
              </p>

              <p className="mb-6 leading-relaxed">
                It's not detox. It's design.
                People aren't escaping technology; they're redesigning their relationship with it.
              </p>

              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "The key to living well in a high-tech world is to spend much less time using technology."
                <footer className="text-sm mt-2">— Cal Newport, Digital Minimalism: Choosing a Focused Life in a Noisy World (New York: Portfolio / Penguin Random House, 2019), p. 28</footer>
              </blockquote>

              <p className="mb-6 leading-relaxed">
                Offline gatherings offer a framework for that alignment — real, physical spaces that make the absence of screens feel communal, not isolating.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">A Shift in What We Value</h2>

              <p className="mb-6 leading-relaxed">
                If the 2010s were about being visible, the 2020s are about being present.
                A generation raised on dopamine loops is beginning to crave stillness.
                What once seemed niche — yoga, journaling, forest bathing — has become mainstream self-care.
              </p>

              <p className="mb-6 leading-relaxed">
                The luxury industry mirrors this shift.
                According to the Deloitte Global Luxury Trends Report (2024), consumers increasingly equate "luxury" with calm, space, and mindfulness, not material excess.
              </p>

              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "Luxury is no longer what you can buy. It's what you can afford to leave behind."
                <footer className="text-sm mt-2">— Alain de Botton, paraphrased from The Pleasures and Sorrows of Work (London: Penguin, 2009), essay "On Success," p. 167</footer>
              </blockquote>

              <p className="mb-6 leading-relaxed">
                Offline is becoming aspirational — not as a rejection of progress, but as a choice for depth.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Offline as Resistance — and Renewal</h2>

              <p className="mb-6 leading-relaxed">
                Intentional disconnection isn't rebellion for rebellion's sake.
                It's an act of renewal — a way to reclaim attention as a sacred resource.
              </p>

              <p className="mb-6 leading-relaxed">
                When we sit in a circle, share a meal, or walk under trees without documenting it, we practice something profoundly human: presence without performance.
                No algorithm rewards it. No metric tracks it.
                And yet, it's exactly what our collective psyche craves.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Back to the Forest</h2>

              <p className="mb-6 leading-relaxed">
                At Mystic Forest, we see this movement not as a retreat from the digital world, but as a return to balance within it.
                We use technology — but we refuse to let it use us.
              </p>

              <p className="mb-6 leading-relaxed">
                We create spaces where the human nervous system can breathe again: retreats, workshops, and gatherings that reconnect people with rhythm, nature, and genuine togetherness.
              </p>

              <p className="mb-6 leading-relaxed">
                Offline isn't about disconnection.
                It's about reconnection — to self, to others, to what truly matters.
              </p>

              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "In the end, the point of gathering is not to escape our lives, but to return to them more fully awake."
                <footer className="text-sm mt-2">— Pico Iyer, The Art of Stillness: Adventures in Going Nowhere (New York: TED Books / Simon & Schuster, 2014), p. 56</footer>
              </blockquote>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h3 className="text-2xl font-serif font-light mt-12 mb-6">References</h3>

              <ul className="space-y-2 mb-6 text-sm">
                <li>Hari, J. (2018). <em>Lost Connections: Why You're Depressed and How to Find Hope</em>. London: Bloomsbury.</li>
                <li>Turkle, S. (2015). <em>Reclaiming Conversation: The Power of Talk in a Digital Age</em>. New York: Penguin Press.</li>
                <li>Newport, C. (2019). <em>Digital Minimalism: Choosing a Focused Life in a Noisy World</em>. New York: Portfolio / Penguin Random House.</li>
                <li>de Botton, A. (2009). <em>The Pleasures and Sorrows of Work</em>. London: Penguin.</li>
                <li>Porges, S. (2011). <em>The Polyvagal Theory</em>. New York: Norton.</li>
                <li>Iyer, P. (2014). <em>The Art of Stillness: Adventures in Going Nowhere</em>. New York: TED Books / Simon & Schuster.</li>
                <li>Deloitte (2024). <em>Global Luxury Trends Report</em>.</li>
                <li>DataReportal (2024). <em>Digital 2024: Global Overview Report</em>.</li>
                <li>American Psychological Association (2023). <em>Loneliness and Social Connection in the Digital Era</em>.</li>
                <li>Recording Industry Association of America (RIAA, 2023). <em>Annual Sales Data</em>.</li>
              </ul>

              <p className="text-center text-lg italic mt-12 text-muted-foreground">
                Offline is not the absence of something — it's the presence of everything that matters.
              </p>
            </div>

            {/* Social Share */}
            <div className="mt-12 pt-8 border-t border-border">
              <SocialShare 
                url={`https://mysticforest.club/articles/offline-gatherings`}
                title={article.title}
                description="Exploring the cultural rise of intentional offline gatherings — a growing movement towards analog, in-person experiences as a counterbalance to digital saturation."
              />
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default OfflineGatherings;
